import React, { useState } from 'react';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Form } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from 'react-router-dom';
import LoginFormFields from './LoginFormFields';
import LoginFormActions from './LoginFormActions';
import { useAuth } from '@/context/SupabaseAuthContext';
import { supabase } from "@/integrations/supabase/client";

const formSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address." }),
  password: z.string().min(6, { message: "Password must be at least 6 characters." }),
  rememberMe: z.boolean().default(false),
  captcha: z.string().optional(),
});

export type LoginFormValues = z.infer<typeof formSchema>;

interface LoginFormProps {
  isSaasInstance?: boolean;
  showCaptcha?: boolean;
  onLogin?: (role: string, token: string) => void;
  onError?: (error: Error) => void;
  onLoginStart?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ 
  isSaasInstance = false,
  showCaptcha = false,
  onLogin,
  onError,
  onLoginStart
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [captchaValue, setCaptchaValue] = useState("");
  const { signIn } = useAuth();
  
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
      captcha: "",
    },
  });

  const onSubmit = async (data: LoginFormValues) => {
    
    if (onLoginStart) {
      onLoginStart();
    }
    setIsLoading(true);
    try {
      if (showCaptcha && data.captcha !== captchaValue) {
        form.setError("captcha", { 
          type: "manual", 
          message: "Invalid captcha. Please try again." 
        });
        setIsLoading(false);
        return;
      }

      const { error } = await signIn(data.email, data.password);
      
      if (error) {
        throw new Error(error.message);
      }
      
      // Login successful
      
      toast({
        title: "Login Successful",
        description: "Welcome back!",
      });
      
      if (onLogin) {
        onLogin('user', 'supabase-token');
      } else {
        // Redirect will be handled by the auth context automatically
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: "Login Failed",
        description: error instanceof Error ? error.message : "Invalid credentials",
        variant: "destructive",
      });
      if (onError && error instanceof Error) {
        onError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCaptchaGenerate = (text: string) => {
    setCaptchaValue(text);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <LoginFormFields 
          form={form} 
          onCaptchaGenerate={handleCaptchaGenerate}
          showCaptcha={showCaptcha}
        />
        <LoginFormActions
          isLoading={isLoading}
          isSaasInstance={isSaasInstance}
          form={form}
        />
      </form>
    </Form>
  );
};

export default LoginForm;
